import { FC } from "react";
import { Card, Button, Icon } from "@aidkitorg/component-library";
import { useLocalizedStrings } from "../../../../../../Localization";
import type { ActiveCallInfo } from "../../../shared/types";

interface ActiveCallViewProps {
  activeCallInfo: ActiveCallInfo;
  onShowKeypad: () => void;
  onHangup: () => void;
  onToggleMute: () => void;
  requireConsentToRecordCalls: any;
  consentGranted: boolean | null;
  isRecording: boolean;
}

/**
 * Component displaying active call information and controls
 */
export const ActiveCallView: FC<ActiveCallViewProps> = ({
  activeCallInfo,
  onShowKeypad,
  onHangup,
  onToggleMute,
  requireConsentToRecordCalls,
  consentGranted,
  isRecording,
}) => {
  const L = useLocalizedStrings();

  const connectedToLabel = L.applicant.comms.connected_to.replace("$number", activeCallInfo.phoneNumber);

  return (
    <>
      <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">{L.applicant.comms.active_call}</h3>
      {/* Recording Status Indicators */}
      <div className="space-y-4">
        {/* Timer Display */}
        <div className="text-center">
          <div className="text-3xl font-mono font-bold">{activeCallInfo.duration}</div>
          <div className="text-sm text-gray-500 mt-1">{connectedToLabel}</div>
          {requireConsentToRecordCalls && (
            <div className="mb-3">
              {consentGranted && isRecording ? (
                <div className="flex justify-center items-center gap-2 text-sm text-red-600">
                  <div className="w-2.5 h-2.5 bg-red-600 rounded-full animate-pulse"></div>
                  <div>{L.applicant.comms.consent.recording_call}</div>
                </div>
              ) : (
                <div className="flex justify-center items-center gap-2 text-sm text-gray-500">
                  <div>{L.applicant.comms.consent.call_in_progress_not_recording}</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="space-y-2">
          {/* Show Keypad Button */}
          <Button variant="secondary" className="w-full" onClick={onShowKeypad} icon={<Icon name="PhoneIcon" />}>
            {L.applicant.comms.show_keypad}
          </Button>

          {/* Mute/Unmute Button */}
          <Button
            variant="secondary"
            className="w-full"
            onClick={onToggleMute}
            icon={<Icon name={activeCallInfo.isMuted ? "SpeakerXMarkIcon" : "SpeakerWaveIcon"} />}
          >
            {activeCallInfo.isMuted ? L.applicant.comms.unmute : L.applicant.comms.mute}
          </Button>

          {/* Hangup Button */}
          <Button variant="danger" className="w-full" onClick={onHangup} icon={<Icon name="PhoneXMarkIcon" />}>
            {L.applicant.comms.hangup}
          </Button>
        </div>
      </div>
    </>
  );
};
